/**
 * §7 簡易予測の一括取込プレビュー フロー hook (simple-methods AC-005a/006a,
 * AC-033/035, ADR demand-forecast-import-preview-only)。
 *
 * ツールバー「予測方法」select → (WMA系のみ) 設定ダイアログ
 * (SimpleForecastSettingsDialog) → 取込確認ダイアログ (ImportConfirmDialog) →
 * PreviewSimpleForecast mutation 実行、の一連の state + handler を集約する。
 * コピー系 (単純前日コピー/同区分前週コピー) は設定ダイアログを経由せず取込確認へ直行する。
 *
 * scope 分岐 (AC-08/AC-033/034): targetScope='RETAILER' のときは従来通り単一小売へ
 * scope=RETAIL で preview する。targetScope='BG_TOTAL' のときは scope=BG_TOTAL を
 * 送るだけで良い (対象小売は backend が case 内訳別実績の計算対象から導出する、§5「BG_TOTAL の
 * 対象小売の列挙元」— 旧実装のような bg_targets の client 側構築は不要)。応答の
 * bg_results を applyPreviewSimpleForecastBgResults で複数小売へ一括反映する。
 *
 * AC-10: preview は draft へ書込まない。応答は index.tsx が保持する editRows の
 * 未保存バッファへ適用されるだけで baseline は更新されない (= 全コマ dirty 化)。
 * 楽観ロックは不要 (書込みが無いため expected_updated_at を持たない)。
 *
 * grid state への反映 (applyImportedDraft / applyPreviewSimpleForecastBgResults) は呼び出し元
 * (index.tsx) が持つため callback として受け取る。
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { ConnectError } from '@connectrpc/connect'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { AggregationScope } from '@ebalance/proto/bffuser/v1/demand_confirm_record_model_pb'
import type { SimpleForecastEntryDefault } from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import {
  PreviewSimpleForecastRequestSchema,
  SimpleForecastEntrySchema,
  SimpleForecastMethod,
  WeekdayCategory,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { DecimalSchema } from '@ebalance/proto/google/type/decimal_pb'
import { useTranslation } from 'react-i18next'

import { appToaster } from '~/libs/toaster'
import { useGetSimpleForecastEntryDefaults } from '~/pages/DemandForecastDaily/hooks/useGetSimpleForecastEntryDefaults'
import {
  type SourceDateViewMonth,
  type UnavailableSourceDates,
  useListSimpleForecastUnavailableSourceDates,
} from '~/pages/DemandForecastDaily/hooks/useListSimpleForecastUnavailableSourceDates'
import { usePreviewSimpleForecast } from '~/pages/DemandForecastDaily/hooks/usePreviewSimpleForecast'
import { canMutate } from '~/pages/DemandForecastDaily/models/canMutate'
import { runImportMutation } from '~/pages/DemandForecastDaily/models/runImportMutation'
import {
  resolveEntryDefaultsDetailedErrorMessage,
  resolveSimplePreviewDetailedErrorMessage,
} from '~/pages/DemandForecastDaily/models/serverError'
import {
  isWmaSimpleForecastMethod,
  optionValueToSimpleForecastMethod,
  simpleForecastMethodLabel,
  simpleForecastMethodToOptionValue,
} from '~/pages/DemandForecastDaily/models/simpleForecast'
import type {
  BgSimpleForecastPreview,
  ImportedDraftResult,
  RetailerEditRow,
  TargetScope,
} from '~/pages/DemandForecastDaily/models/types'

type Params = {
  caseId: string | undefined
  selectedAreaCode: AreaCode | undefined
  section: WorkflowSection
  targetDate: ProtoDate | undefined
  /** BG全体 / 小売ビュー切替 (AC-08: BG_TOTAL でも取込可) */
  targetScope: TargetScope
  /** 現在選択中の小売 (小売ビューのみ使用。§4 主要意思決定) */
  selectedRetailerBusinessCd: string | undefined
  /** RETAIL scope の preview 適用先の存在確認にのみ使う (楽観ロックは preview に無い) */
  editRows: Record<string, RetailerEditRow>
  /** preview 応答を未保存バッファ (grid state) へ反映する (index.tsx が保持) */
  applyImportedDraft: (
    retailerBusinessCd: string,
    result: ImportedDraftResult,
  ) => boolean
  /** BG_TOTAL scope preview 応答 (bg_results) を複数小売へ一括反映する (index.tsx が保持) */
  applyPreviewSimpleForecastBgResults: (
    preview: BgSimpleForecastPreview,
  ) => boolean
  onPreviewApplied: (notice: {
    zeroedRetailerBusinessCds: string[]
    sourceDates: ProtoDate[]
  }) => void
}

export type UseSimpleForecastImportFlowResult = {
  /** ツールバー予測方法 select の現在値 (option value 文字列) */
  methodOptionValue: string
  /** 予測方法選択 (§7 手法選択 UI の起点、AC-033/035)。メニュー項目クリックで呼ぶ */
  onMethodChange: (value: string) => void
  /** WMA 設定ダイアログの開閉状態 */
  isSettingsOpen: boolean
  /** 設定ダイアログに渡す method (未選択時は UNSPECIFIED) */
  settingsMethod: SimpleForecastMethod
  /** GetSimpleForecastEntryDefaults 応答の entries */
  entryDefaults: SimpleForecastEntryDefault[]
  isEntryDefaultsLoading: boolean
  entryDefaultsErrorMessage: string | undefined
  /** 対象日の日区分 (AC-006c 行警告用、SimpleForecastSettingsDialog へ渡す) */
  targetWeekdayCategory: WeekdayCategory
  /** 対象日の特異グループ (需要扱いが特異の時だけ 1〜10。AC-DFM-016 の文言用) */
  targetSingularGroup: number | undefined
  /** 取込元日付の picker が表示中の範囲の実績の無い日 (AC-DFM-017) */
  unavailableSourceDates: UnavailableSourceDates
  /** 取込元日付の picker の表示月が決まった時 (開いた時・月送り) */
  onSourceDateViewMonthChange: (year: number, month: number) => void
  /** 設定ダイアログの日付変更 (日タイプ再解決トリガー) */
  onSettingsDateChange: (dates: ProtoDate[]) => void
  /** 設定ダイアログ「適用」→ 取込確認ダイアログへ (design doc §5) */
  applySettings: (
    entries: Array<{ sourceDate: ProtoDate; weight: number }>,
  ) => void
  /** 設定ダイアログキャンセル */
  cancelSettings: () => void
  /** §7 取込確認ダイアログの開閉状態 */
  isImportConfirmOpen: boolean
  /** 取込確認ダイアログの methodName (AC-035: 手法名のみ表示、「予測エンジン」表現なし) */
  methodName: string | undefined
  /** 取込確認ダイアログキャンセル */
  cancelImportConfirm: () => void
  /** 取込確認ダイアログの「取込」実行 (preview 取得 → バッファ適用) */
  executeImportConfirm: () => Promise<void>
  /** mutation 実行中かどうか (ImportConfirmDialog の isLoading に渡す) */
  isImporting: boolean
}

/** §7 簡易予測プレビューフローの state + handler をまとめて返す。 */
export function useSimpleForecastImportFlow({
  caseId,
  selectedAreaCode,
  section,
  targetDate,
  targetScope,
  selectedRetailerBusinessCd,
  editRows,
  applyImportedDraft,
  applyPreviewSimpleForecastBgResults,
  onPreviewApplied,
}: Params): UseSimpleForecastImportFlowResult {
  const { t } = useTranslation()

  const [selectedMethod, setSelectedMethod] = useState<
    SimpleForecastMethod | undefined
  >(undefined)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isImportConfirmOpen, setIsImportConfirmOpen] = useState(false)
  // WMA 系の日付変更時の再解決に使う日付配列。[] = 初期表示 (backend 既定導出)
  const [wmaResolveDates, setWmaResolveDates] = useState<ProtoDate[]>([])
  // 設定ダイアログ「適用」で確定した Entry 群 (取込確認ダイアログ経由で実行時に使用)
  const [pendingWmaEntries, setPendingWmaEntries] = useState<
    Array<{ sourceDate: ProtoDate; weight: number }>
  >([])

  const {
    data: entryDefaultsData,
    error: entryDefaultsError,
    isLoading: isEntryDefaultsLoading,
  } = useGetSimpleForecastEntryDefaults({
    areaCode: selectedAreaCode,
    caseId,
    targetDate,
    method: selectedMethod,
    targetScope,
    retailerBusinessCd: selectedRetailerBusinessCd,
    dates: wmaResolveDates,
    enabled: isSettingsOpen,
  })

  const [sourceDateViewMonth, setSourceDateViewMonth] = useState<
    SourceDateViewMonth | undefined
  >(undefined)
  const unavailableSourceDates = useListSimpleForecastUnavailableSourceDates({
    areaCode: selectedAreaCode,
    caseId,
    targetDate,
    method: selectedMethod,
    targetScope,
    retailerBusinessCd: selectedRetailerBusinessCd,
    viewMonth: sourceDateViewMonth,
    enabled: isSettingsOpen,
  })
  const onSourceDateViewMonthChange = useCallback(
    (year: number, month: number): void => {
      setSourceDateViewMonth((prev) =>
        prev?.year === year && prev.month === month ? prev : { year, month },
      )
    },
    [],
  )

  const { mutateAsync: previewSimpleForecastAsync, isPending: isImporting } =
    usePreviewSimpleForecast()
  const isEntryDefaultsCompatible =
    entryDefaultsData?.perBreakdownActuals === true

  const currentCoordinatesRef = useRef({
    caseId,
    areaCode: selectedAreaCode,
    targetDate,
    targetScope,
    retailerBusinessCd: selectedRetailerBusinessCd,
  })
  useEffect(() => {
    currentCoordinatesRef.current = {
      caseId,
      areaCode: selectedAreaCode,
      targetDate,
      targetScope,
      retailerBusinessCd: selectedRetailerBusinessCd,
    }
  }, [
    caseId,
    selectedAreaCode,
    targetDate,
    targetScope,
    selectedRetailerBusinessCd,
  ])

  /**
   * 予測方法 select の onChange。コピー系 (単純前日コピー/同区分前週コピー) は
   * 取込確認ダイアログへ直行、WMA 系 (加重移動平均/同区分加重平均) は設定ダイアログを経由する。
   */
  const onMethodChange = useCallback((value: string): void => {
    const method = optionValueToSimpleForecastMethod(value)
    if (method === undefined) return
    setSelectedMethod(method)
    if (isWmaSimpleForecastMethod(method)) {
      setWmaResolveDates([])
      setSourceDateViewMonth(undefined)
      setIsSettingsOpen(true)
    } else {
      setIsImportConfirmOpen(true)
    }
  }, [])

  const onSettingsDateChange = useCallback((dates: ProtoDate[]): void => {
    setWmaResolveDates(dates)
  }, [])

  const applySettings = useCallback(
    (entries: Array<{ sourceDate: ProtoDate; weight: number }>): void => {
      if (
        isEntryDefaultsLoading ||
        entryDefaultsError !== null ||
        !isEntryDefaultsCompatible
      )
        return
      setPendingWmaEntries(entries)
      setIsSettingsOpen(false)
      setIsImportConfirmOpen(true)
    },
    [entryDefaultsError, isEntryDefaultsLoading, isEntryDefaultsCompatible],
  )

  const cancelSettings = useCallback((): void => {
    setIsSettingsOpen(false)
    setSelectedMethod(undefined)
    setWmaResolveDates([])
  }, [])

  const cancelImportConfirm = useCallback((): void => {
    setIsImportConfirmOpen(false)
    setSelectedMethod(undefined)
    setPendingWmaEntries([])
    setWmaResolveDates([])
  }, [])

  const executeImportConfirm = useCallback(async (): Promise<void> => {
    // 過去日 gate (PR #5573 review, devin index.tsx:1307): 日跨ぎ直後の残存経路で
    // PreviewSimpleForecast (read-only RPC) を無駄に発行しないよう、取込確認の実行
    // 入口 (簡易予測フローの write/RPC orchestration seam) で実時刻から過去日判定し
    // 早期 return する。draft への反映 (applyImportedDraft /
    // applyPreviewSimpleForecastBgResults) は useForecastDraftEditor 側でも gate 済み
    // (二重防御) だが、ここで止めることで RPC 発火自体を抑止し、旧 gate と同等の挙動
    // (過去日は通信なしで即クローズ) に戻す。確定 (handleConfirmExecute) と同様、編集
    // hook を経由しない write/RPC 経路はその orchestration 入口に gate を置く。
    // 判定式は models/canMutate に集約 (5 seam を単一 predicate 経由に統一)。
    if (!canMutate(targetDate)) {
      cancelImportConfirm()
      return
    }
    if (
      selectedAreaCode === undefined ||
      targetDate === undefined ||
      caseId === undefined ||
      selectedMethod === undefined
    )
      return

    const isBgTotal = targetScope === 'BG_TOTAL'
    if (!isBgTotal && selectedRetailerBusinessCd === undefined) return
    // BG_TOTAL: 対象小売は backend が内訳別実績の計算対象から導出する (§5)。RETAIL: 選択中の
    // 小売が現在の editRows に存在することだけ確認する (楽観ロックは preview に無い)。
    if (!isBgTotal && editRows[selectedRetailerBusinessCd ?? ''] === undefined)
      return
    const retailerBusinessCd = isBgTotal
      ? undefined
      : selectedRetailerBusinessCd
    const scope = isBgTotal
      ? AggregationScope.BG_TOTAL
      : AggregationScope.RETAIL
    const requestCoordinates = {
      caseId,
      areaCode: selectedAreaCode,
      targetDate,
      targetScope,
      retailerBusinessCd: selectedRetailerBusinessCd,
    }

    const method = selectedMethod
    const entries = isWmaSimpleForecastMethod(method)
      ? pendingWmaEntries.map((e) =>
          create(SimpleForecastEntrySchema, {
            sourceDate: e.sourceDate,
            weight: create(DecimalSchema, { value: String(e.weight) }),
          }),
        )
      : []

    // reason 別文言解決の受け皿 (matches で解決し handle で読む、runImportMutation
    // の ImportMutationErrorHandler は handle() に err を渡さない契約のため
    // クロージャで橋渡しする)。
    let resolvedReasonMessage: string | undefined
    await runImportMutation({
      mutateAsync: () =>
        previewSimpleForecastAsync(
          create(PreviewSimpleForecastRequestSchema, {
            caseId,
            areaCode: selectedAreaCode,
            section,
            targetDate,
            retailerBusinessCd,
            method,
            entries,
            scope,
            supportsUnattributed: true,
          }),
        ),
      applyResult: (response) => {
        const current = currentCoordinatesRef.current
        if (
          current.caseId !== requestCoordinates.caseId ||
          current.areaCode !== requestCoordinates.areaCode ||
          current.targetScope !== requestCoordinates.targetScope ||
          current.retailerBusinessCd !==
            requestCoordinates.retailerBusinessCd ||
          current.targetDate?.year !== requestCoordinates.targetDate?.year ||
          current.targetDate?.month !== requestCoordinates.targetDate?.month ||
          current.targetDate?.day !== requestCoordinates.targetDate?.day
        )
          return
        if (!response.perBreakdownActuals) {
          appToaster.create({
            type: 'error',
            title: t(
              'page:demandForecastDaily.save.serverError.reason.clientUpdateRequired',
            ),
          })
          return
        }
        if (
          (isBgTotal && response.result.case !== 'bgTotal') ||
          (!isBgTotal && response.result.case !== 'retail')
        ) {
          appToaster.create({
            type: 'error',
            title: t(
              'page:demandForecastDaily.save.serverError.reason.clientUpdateRequired',
            ),
          })
          return
        }
        if (response.result.case === 'bgTotal') {
          const bgTotal = response.result.value
          // provider/providerLabel は手法メタが全小売共通のため top-level
          // (BgTotalPreviewResult) に乗っており、per-retailer の bgResults には
          // 含まれない。applyPreviewSimpleForecastBgResults へは各 BgResultLike へ複製して
          // 渡す (review 指摘 PR #3199: BG_TOTAL preview の取込元情報を保存
          // バッファへ適用できるようにする)。
          if (bgTotal.unattributed === undefined) {
            appToaster.create({
              type: 'error',
              title: t(
                'page:demandForecastDaily.save.serverError.reason.clientUpdateRequired',
              ),
            })
            return
          }
          const applied = applyPreviewSimpleForecastBgResults({
            retailerResults: bgTotal.bgResults.map((r) => ({
              retailerBusinessCd: r.retailerBusinessCd,
              forecastSlots: r.forecastSlots,
              providerSlots: r.providerSlots,
              provider: bgTotal.provider,
              providerOther: bgTotal.providerOther,
              providerLabel: bgTotal.providerLabel,
            })),
            unattributed: {
              forecastSlots: bgTotal.unattributed.forecastSlots,
              providerSlots: bgTotal.unattributed.providerSlots,
              provider: bgTotal.provider,
              providerOther: bgTotal.providerOther,
              providerLabel: bgTotal.providerLabel,
            },
          })
          if (!applied)
            appToaster.create({
              type: 'error',
              title: t(
                'page:demandForecastDaily.save.serverError.reason.clientUpdateRequired',
              ),
            })
          else
            onPreviewApplied({
              zeroedRetailerBusinessCds: response.zeroedRetailerBusinessCds,
              sourceDates: response.sourceDates,
            })
        } else if (
          response.result.case === 'retail' &&
          retailerBusinessCd !== undefined
        ) {
          if (applyImportedDraft(retailerBusinessCd, response.result.value)) {
            onPreviewApplied({
              zeroedRetailerBusinessCds: response.zeroedRetailerBusinessCds,
              sourceDates: response.sourceDates,
            })
          } else {
            appToaster.create({
              type: 'error',
              title: t(
                'page:demandForecastDaily.save.serverError.reason.clientUpdateRequired',
              ),
            })
          }
        }
      },
      onSuccess: () => {
        setIsImportConfirmOpen(false)
        setSelectedMethod(undefined)
        setPendingWmaEntries([])
        setWmaResolveDates([])
      },
      extraErrorHandlers: [
        {
          // ①上位断面不在 (AC-DFM-013) / ②実績不足 (AC-DFM-008) / ③同一特異グループの
          // 特異日候補不足 (AC-DFM-006a-1) / readonly gate (AC-DFC-007) を reason
          // で判別する (旧実装の method 手動分岐を廃止, Design Note
          // demand-forecast-non-confirm-reason-registry.md Decision #2)。
          matches: (err) => {
            if (!(err instanceof ConnectError)) return false
            resolvedReasonMessage = resolveSimplePreviewDetailedErrorMessage(
              err,
              t,
              simpleForecastMethodLabel(method, t),
            )
            return resolvedReasonMessage !== undefined
          },
          handle: () => {
            appToaster.create({
              type: 'error',
              title: resolvedReasonMessage,
            })
            setIsImportConfirmOpen(false)
          },
        },
      ],
      // preview は書込みを伴わないため楽観ロック衝突 (ABORTED) は発生しない契約
      // だが、防御的に一般エラー扱いにフォールバックする。
      onAbortedConflict: () => {
        appToaster.create({
          type: 'error',
          title: t('page:demandForecastDaily.simpleForecast.importError'),
        })
      },
      onOtherError: () => {
        appToaster.create({
          type: 'error',
          title: t('page:demandForecastDaily.simpleForecast.importError'),
        })
      },
    })
  }, [
    targetScope,
    selectedRetailerBusinessCd,
    selectedAreaCode,
    targetDate,
    caseId,
    selectedMethod,
    editRows,
    pendingWmaEntries,
    previewSimpleForecastAsync,
    applyImportedDraft,
    applyPreviewSimpleForecastBgResults,
    onPreviewApplied,
    section,
    t,
    cancelImportConfirm,
  ])

  return {
    methodOptionValue: simpleForecastMethodToOptionValue(selectedMethod),
    onMethodChange,
    isSettingsOpen,
    settingsMethod: selectedMethod ?? SimpleForecastMethod.UNSPECIFIED,
    entryDefaults: isEntryDefaultsCompatible
      ? (entryDefaultsData?.entries ?? [])
      : [],
    isEntryDefaultsLoading,
    entryDefaultsErrorMessage:
      entryDefaultsError === null
        ? entryDefaultsData !== undefined && !isEntryDefaultsCompatible
          ? t(
              'page:demandForecastDaily.save.serverError.reason.clientUpdateRequired',
            )
          : undefined
        : (resolveEntryDefaultsDetailedErrorMessage(entryDefaultsError, t) ??
          t('page:demandForecastDaily.simpleForecast.settingsLoadError')),
    targetWeekdayCategory:
      entryDefaultsData?.targetWeekdayCategory ?? WeekdayCategory.UNSPECIFIED,
    targetSingularGroup: entryDefaultsData?.targetSingularGroup,
    unavailableSourceDates,
    onSourceDateViewMonthChange,
    onSettingsDateChange,
    applySettings,
    cancelSettings,
    isImportConfirmOpen,
    methodName:
      selectedMethod !== undefined
        ? simpleForecastMethodLabel(selectedMethod, t)
        : undefined,
    cancelImportConfirm,
    executeImportConfirm,
    isImporting,
  }
}
